import React, { useEffect, useState, useCallback, useContext } from "react";
import {
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Toolbar,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TablePagination,
  Box,
  Divider,
} from "@mui/material";
import { MyDatePicker } from "../../components/my-date-picker/MyDatePicker";
import { MyButton } from "../../components/my-button/MyButton";
import { DisplayClaimType, lngs } from "../../Constant";
import ClaimApiService from "../../adapters/xhr/ClaimApiService";
import { MyNumberField } from "../../components/my-number-field/MyNumberField";
import { MySelect } from "../../components/my-select/MySelect";
import { formatDate, formatTime } from "../../Utils";
import AdministrationApiService from "../../adapters/xhr/AdministrationApiService";
import { useTranslation } from "react-i18next";
import MyContext from "../../contexts/MyContext";
import { MyTextField } from "../../components/my-text-field/MyTextField";

const EnhancedTableToolbar = (props) => {
  return (
    <Toolbar>
      <Typography sx={{ flex: "1 1 100%" }} variant="h6" id="tableTitle" component="div">
        {props.title}
      </Typography>
    </Toolbar>
  );
};
export const TrainOverviewPage = () => {
  const { t, i18n } = useTranslation();
  const columns = [
    { id: "CaseNumber", label: t("case_number"), minWidth: 170, sortable: true },
    { id: "ClaimStatus", label: t("status"), minWidth: 170, sortable: true },
    { id: "Created", label: t("registred_datetime"), minWidth: 180, sortable: true },
    { id: "TrainNumber", label: t("train_number"), minWidth: 170, sortable: true },
    { id: "DepartureDate", label: t("departure_date"), minWidth: 170, sortable: true },
    { id: "ReimbursementSEK", label: t("reimbursement_SEK"), minWidth: 170, sortable: true },
    { id: "ReceiptTotalCost", label: t("receipttotalcost_SEK"), minWidth: 170 },
    { id: "TicketPriceSEK", label: t("ticket_price_SEK"), minWidth: 170 },
    { id: "TrafikverketDelayMin", label: t("trafikverket_delay_min"), minWidth: 170 },
    { id: "DepartureStation", label: t("departure_station"), minWidth: 180, sortable: false },
    { id: "ArrivalStation", label: t("arrival_station"), minWidth: 180, sortable: false },
    { id: "Comment", label: t("comment"), minWidth: 300, sortable: true },
    { id: "TicketValidFrom", label: t("ticket_valid_from"), minWidth: 180 },
    { id: "TicketValidTo", label: t("ticket_valid_to"), minWidth: 180 },
    { id: "TicketNumber", label: t("ticket_number"), minWidth: 170, sortable: true },
    { id: "SentToTopDesk", label: t("sent_to_TopDesk"), minWidth: 170, sortable: true },
    { id: "ChangedTrain", label: t("trip_include_change"), minWidth: 170, sortable: true },
    { id: "HasCancellationsInPreviousStations", label: t("train_is_cancelled"), minWidth: 170, sortable: true },
    { id: "IsShortTrain", label: t("is_short_train"), minWidth: 170, sortable: true },
    { id: "Reason", label: t("reason"), minWidth: 350, sortable: false },
  ];
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [claims, setClaims] = useState([]);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [ticketNumber, setTicketNumber] = useState();
  const [trainNumber, setTrainNumber] = useState();
  const [claimStatus, setClaimStatus] = useState([]);
  const [claimStatusOptions, setClaimStatusOptions] = useState([]);
  const [sortBy, setSortBy] = useState("Created");
  const [ascending, setAscending] = useState(false);
  const [showMore, setShowMore] = useState([]);
  const myContext = useContext(MyContext);

  const fetchTrainStations = useCallback(async () => {
    let response = await AdministrationApiService.getTrainStations();
    if (response.isSuccess) {
      myContext.changeTrainStations(response.data);
    }
  }, []);

  const handleSort = async (field, ascending) => {
    if (field === "DepartureStation") field = "DepartureStationId";
    if (field === "ArrivalStation") field = "ArrivalStationId";
    if (field === "Status") field = i18n.language === lngs.en.code ? "ClaimStatus" : "ClaimStatusSwedish";
    setSortBy(field);
    setAscending(ascending);
  };

  const fetchClaimStatus = useCallback(async () => {
    let response = await ClaimApiService.getClaimStatus();
    if (response.isSuccess) {
      let data = response.data.map((option) => {
        let rObj = {};
        rObj["key"] = option.id;
        rObj["value"] = i18n.language === lngs.en.code ? option.name : option.swedishName;
        return rObj;
      });
      setClaimStatusOptions(data);
    }
  }, []);

  useEffect(() => {
    if (!claimStatusOptions || claimStatusOptions.length === 0) fetchClaimStatus();
    if (!myContext.trainStations || myContext.trainStations.length === 0) fetchTrainStations();
  }, [fetchClaimStatus, fetchTrainStations]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeClaimStatus = (value) => {
    setClaimStatus(value);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const fetchClaims = async () => {
    let payload = {
      claimStatus: claimStatus.map((item) => item.key),
      pageNumber: page + 1,
      pageSize: rowsPerPage,
      ascending: ascending,
      sortBy: sortBy,
      displayType: DisplayClaimType.Overview,
    };
    if (trainNumber) {
      payload.trainNumber = Number(trainNumber);
    }
    if (ticketNumber) {
      payload.ticketNumber = ticketNumber;
    }
    if (startDate) {
      payload.startDate = formatDate(startDate);
    }
    if (endDate) {
      payload.endDate = formatDate(endDate);
    }
    let response = await ClaimApiService.getAll(payload);
    if (response?.isSuccess) {
      let data = response.data.items.map((option) => {
        let rObj = {};
        rObj["CaseNumber"] = option.caseNumber;
        rObj["ClaimStatus"] = i18n.language === lngs.en.code ? option.claimStatus?.name : option.claimStatus?.swedishName;
        rObj["Reason"] = option.reason
          ?.split(";")
          .map((item) => t(item))
          .join(" ");
        rObj["Created"] = `${formatDate(option.created)} ${formatTime(option.created, true)}`;
        rObj["TrainNumber"] = option.trainNumber;
        rObj["DepartureDate"] = `${formatDate(option.departureDate)} ${formatTime(option.departureDate, false)}`;
        rObj["ReimbursementSEK"] = option.reimbursementSEK;
        rObj["ReceiptTotalCost"] = option.receiptTotalCost;
        rObj["TicketPriceSEK"] = option.ticketPriceSEK;
        rObj["TrafikverketDelayMin"] = option.trafikverketDelayMinutes;
        rObj["DepartureStation"] = myContext.trainStations.find((item) => item.id === option.departureStationId)?.name;
        rObj["ArrivalStation"] = myContext.trainStations.find((item) => item.id === option.arrivalStationId)?.name;
        rObj["Comment"] = option.comment;
        rObj["TicketValidFrom"] = `${formatDate(option.ticketValidFrom)} ${formatTime(option.ticketValidFrom, true)}`;
        rObj["TicketValidTo"] = `${formatDate(option.ticketValidTo)} ${formatTime(option.ticketValidTo, true)}`;
        rObj["TicketNumber"] = option.ticketNumber;
        rObj["SentToTopDesk"] = `${formatDate(option.sentToTopDesk)} ${formatTime(option.sentToTopDesk, true)}`;
        rObj["ChangedTrain"] = `${option.changedTrain ? t("yes") : t("no")}`;
        rObj["HasCancellationsInPreviousStations"] = option.hasCancellationsInPreviousStations ? t("yes") : t("no");
        rObj["IsShortTrain"] = `${option.isShortTrain ? t("yes") : t("no")}`;
        return rObj;
      });
      setClaims(data);
      setTotalPages(response.data.totalCount);
    }
  };

  useEffect(() => {
    if (myContext.trainStations != null && myContext.trainStations.length > 0) fetchClaims();
  }, [myContext.trainStations, page, ascending, sortBy, rowsPerPage]);

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <Box sx={{ display: "flex" }}>
        <MyDatePicker
          size="small"
          onAccept={(date) => setStartDate(date.toString())}
          value={startDate}
          label={t("from")}
          style={{ width: "12rem", margin: "1rem 0 1rem 1rem" }}
        />
        <MyDatePicker
          size="small"
          onAccept={(date) => setEndDate(date.toString())}
          value={endDate}
          label={t("to")}
          style={{ width: "12rem", margin: "1rem 0 1rem 1rem" }}
        />
        <MySelect
          size="small"
          style={{ width: "12rem", margin: "1rem 0 1rem 1rem" }}
          label={t("status")}
          onChange={handleChangeClaimStatus}
          options={claimStatusOptions}
          value={claimStatus}
        />
        <MyNumberField
          size="small"
          label={t("train_number")}
          onChange={(e) => setTrainNumber(e.target.value)}
          value={trainNumber}
          style={{ width: "12rem", margin: "1rem 0 1rem 1rem" }}
        />
        <MyTextField
          size="small"
          value={ticketNumber}
          label={t("ticket_number")}
          onChange={setTicketNumber}
          style={{ width: "12rem", margin: "1rem 0 1rem 1rem" }}
        />
        <MyButton
          text={t("search")}
          color="primary"
          onClick={(e) => {
            setPage(0);
            fetchClaims();
          }}
          style={{ width: "7rem", margin: "1rem 0 1rem 1rem" }}
        />
      </Box>
      <Divider />
      <EnhancedTableToolbar title={`${t("claims")} (${totalPages})`} />
      <TableContainer>
        <Table stickyHeader aria-label="sticky table" size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.id} align={column.align} style={{ minWidth: column.minWidth }}>
                  {column.sortable ? (
                    <TableSortLabel active={column.id === sortBy} direction={ascending ? "asc" : "desc"} onClick={(e) => handleSort(column.id, !ascending)}>
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {claims.map((row, key) => {
              return (
                <TableRow hover tabIndex={-1} key={key}>
                  {columns.map((column) => {
                    const value = row[column.id];
                    return (
                      <TableCell key={column.id} align={column.align}>
                        {column.format && typeof value === "number"
                          ? column.format(value)
                          : showMore.some((item) => item == `${key}`)
                          ? value
                          : value?.toString().substring(0, 40)}
                        {value?.toString().length > 40 && (showMore == [] || !showMore.some((item) => item == key)) && (
                          <i className="selector" onClick={() => setShowMore([...showMore, `${key}`])}>
                            {` ...`}
                          </i>
                        )}
                        {value?.toString().length > 40 && showMore != [] && showMore.some((item) => item == key) && (
                          <strong color="primary.main" className="selector" onClick={() => setShowMore([...showMore].filter((item) => item != key))}>
                            {` ${t("show_less")}`}
                          </strong>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={totalPages}
        labelRowsPerPage={t("rows_per_page")}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};
